import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../lib/ui";

const money = (v) =>
  v >= 1e6 ? `$${(v / 1e6).toFixed(1)}m` : v || v === 0 ? `$${Math.round(v).toLocaleString()}` : "—";

const STATUS_STYLE = {
  Complete: "border-ochre text-ochre",
  "In progress": "border-maasai text-maasai",
  Open: "border-white/40 text-white",
  "Not started": "border-white/15 text-zinc-500",
};

const Number_ = ({ label, value, note, testId }) => (
  <div className="border-t border-white/10 pt-3" data-testid={testId}>
    <p className="text-[0.58rem] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
    <p className="mono mt-1 text-xl text-white">{value}</p>
    {note ? <p className="mt-1 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">{note}</p> : null}
  </div>
);

export const FutsalCourtFeature = () => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/futsal-court").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d) return null;
  const n = d.numbers;

  return (
    <section className="border-t border-zinc-900 bg-black py-16 md:py-24" data-testid="home-futsal-court">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The flagship build</p>
          <div className="mt-4 grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-end">
            <h2 className="display-2">{d.title}</h2>
            <p className="body-copy">{d.standfirst}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <Reveal>
            <div className="border border-white/10 bg-[#0b0b0b] p-7 md:p-9">
              <p className="overline text-ochre" data-testid="futsal-site">
                {d.site.name}
              </p>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80">{d.site.why}</p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-5 md:grid-cols-4" data-testid="futsal-numbers">
              <Number_
                label="Build cost"
                value={money(n.capex_usd)}
                note={`${money(n.capex_low_usd)}–${money(n.capex_high_usd)} range`}
                testId="futsal-capex"
              />
              <Number_
                label="Annual income"
                value={money(n.annual_revenue_usd)}
                note={`${n.cost_recovery_pct}% of running cost`}
                testId="futsal-revenue"
              />
              <Number_
                label="Players a year"
                value={n.players_per_year.toLocaleString()}
                note={`${n.teams} teams`}
                testId="futsal-players"
              />
              <Number_
                label="Jobs and places"
                value={`${n.jobs} + ${n.coaching_places + n.referee_places}`}
                note="Permanent jobs, coaching and refereeing"
                testId="futsal-jobs"
              />
            </div>

            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-zinc-400" data-testid="futsal-why">
              <span className="text-white">Why a court first. </span>
              It is the smallest complete unit of this programme: one surface, one league, one maintenance team, and a
              price a single funder can carry outright.
            </p>
            <p className="mt-4 max-w-2xl text-xs leading-relaxed text-zinc-500">{d.note}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/news/${d.story_slug}`} className="btn-primary" data-testid="futsal-story-link">
                Read the site survey
              </Link>
              <Link to="/apply" className="btn-ghost" data-testid="futsal-fund-link">
                Fund this court
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <div className="border border-white/10 bg-[#0b0b0b] p-6">
              <p className="overline">Specification</p>
              <dl className="mt-5" data-testid="futsal-spec">
                {d.spec.map((s) => (
                  <div key={s.label} className="border-t border-white/10 py-4 first:border-t-0 first:pt-0">
                    <dt className="text-[0.58rem] font-bold uppercase tracking-[0.18em] text-zinc-500">{s.label}</dt>
                    <dd className="mono mt-1 text-sm text-white">{s.value}</dd>
                    <dd className="mt-1 text-xs leading-relaxed text-zinc-400">{s.detail}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 border border-white/10 bg-[#0b0b0b] p-6">
              <p className="overline">Where it has got to</p>
              <ol className="mt-5" data-testid="futsal-phases">
                {d.phases.map((p) => (
                  <li key={p.phase} className="border-t border-white/10 py-4 first:border-t-0 first:pt-0">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-display text-sm uppercase tracking-tight text-white">{p.phase}</span>
                      <span
                        className={`shrink-0 border px-2 py-1 text-[0.5rem] font-bold uppercase tracking-[0.14em] ${
                          STATUS_STYLE[p.status] || "border-white/15 text-zinc-500"
                        }`}
                      >
                        {p.status}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-zinc-400">{p.detail}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6" data-testid="futsal-sources">
          <span className="text-[0.58rem] font-bold uppercase tracking-[0.2em] text-zinc-400">Sources</span>
          {d.sources.map((s, i) => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`futsal-source-${i}`}
              className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400 hover:text-ochre"
            >
              {s.publisher} <ExternalLink size={11} />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
